import React, { useEffect, useRef, useState } from "react";
import AIChatbotSettingsTabs from "./AIChatbotSettingsTabs";
import useAIConfigs from "@/hooks/useAIConfigs";
import { LoadingSpinner } from "@velaro/velaro-component-lib";
import { useParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface ChatMessage {
  role: "user" | "bot";
  text: string;
  timestamp: Date;
}

function AIChatbotTestHeader({ text }: { text: string }) {
  return (
    <div className="text-slate-800 font-medium leading-normal py-2 border-b-[1px]">
      {text}
    </div>
  );
}

function AIChatbotTestRow({ label, text }: { label: string; text: string }) {
  return (
    <div className="flex py-2">
      <div className="w-1/3 text-slate-500 text-sm font-medium font-['Rubik'] leading-tight">
        {label}
      </div>
      <div className="w-2/3 text-slate-800 text-sm font-normal font-['Rubik'] leading-tight break-words">
        {text || "—"}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-indigo-600 text-xs font-bold">
        AI
      </div>
      <div className="bg-slate-100 rounded-2xl rounded-tl-none px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ message }: { message: ChatMessage }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[75%]">
          <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-none px-4 py-2.5 text-sm leading-relaxed">
            {message.text}
          </div>
          <div className="text-xs text-slate-400 text-right mt-1">
            {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-indigo-600 text-xs font-bold">
        AI
      </div>
      <div className="max-w-[75%]">
        <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-tl-none px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap">
          {message.text}
        </div>
        <div className="text-xs text-slate-400 mt-1">
          {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

export default function AIChatbotSettingsTest() {
  const { id } = useParams();
  const { aiConfigurations, loading } = useAIConfigs();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const api = useApi();
  const toastAlert = useToastAlert();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const config = aiConfigurations.find((c) => c.id === parseInt(id ?? "0"));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking]);

  if (loading) return <LoadingSpinner />;

  if (!config) {
    return (
      <div className="flex flex-col">
        <AIChatbotSettingsTabs selectedTab="Test" aiConfigId={id!} />
        <div className="bg-white p-6 border-[1px] rounded-2xl shadow-sm w-full self-center flex items-center justify-center h-48 text-slate-400">
          Bot configuration not found.
        </div>
      </div>
    );
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || isThinking) return;

    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", text, timestamp: new Date() }
    ]);
    setIsThinking(true);

    try {
      const result = await api.messaging.post("AzureIndexes/ChatQuery", {
        aiConfigId: config!.id,
        query: text
      });

      if (result.ok) {
        const json = await result.json();
        const answer =
          typeof json === "string"
            ? json
            : json.answer ?? json.result ?? JSON.stringify(json);

        setMessages((prev) => [
          ...prev,
          { role: "bot", text: answer || "No response.", timestamp: new Date() }
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text: "Something went wrong — check your bot configuration and index.",
            timestamp: new Date()
          }
        ]);
        toastAlert.displayToast(Severity.Error, "Bot query failed");
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "Unable to reach the AI service. Check your network and try again.",
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsThinking(false);
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function clearConversation() {
    setMessages([]);
    setInput("");
    inputRef.current?.focus();
  }

  return (
    <div className="flex flex-col">
      <AIChatbotSettingsTabs selectedTab="Test" aiConfigId={id!} />

      <div className="bg-white p-6 border-[1px] rounded-2xl shadow-sm w-full self-center flex gap-6" style={{ minHeight: 600 }}>

        {/* Left — settings summary */}
        <div className="w-[320px] flex-shrink-0 border-r border-slate-100 pr-6 overflow-y-auto">
          <AIChatbotTestHeader text="Configuration" />
          <AIChatbotTestRow label="Name" text={config.name} />
          <AIChatbotTestRow label="AI Model" text={config.aiModel} />
          <AIChatbotTestRow label="Language" text={config.language} />
          <AIChatbotTestRow label="Reply Length" text={config.replyLength} />
          <AIChatbotTestRow
            label="Knowledge Restriction"
            text={config.knowledgeRestriction ? "On" : "Off"}
          />
          <AIChatbotTestRow
            label="Follow-up Questions"
            text={config.followUp ? "On" : "Off"}
          />
          <AIChatbotTestRow
            label="Rate Response"
            text={config.rateResponse ? "On" : "Off"}
          />
          <AIChatbotTestHeader text="Instructions" />
          <div className="py-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words">
            {config.prompt || "No instructions set."}
          </div>
        </div>

        {/* Right — chat */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-sm font-medium text-slate-700">
                {config.name || "AI Bot"} — Test Mode
              </span>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Not saved
              </span>
            </div>
            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Clear conversation"
              >
                Clear
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto pr-1" style={{ minHeight: 0, maxHeight: 460 }}>
            {messages.length === 0 && !isThinking && (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                  <span className="text-indigo-400 text-lg font-bold">AI</span>
                </div>
                <p className="text-slate-500 text-sm">
                  Ask the bot anything to test its responses.
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  Results are not saved and do not affect live chats.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <ChatBubble key={i} message={msg} />
            ))}

            {isThinking && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="mt-3 border border-slate-200 rounded-xl bg-slate-50 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message and press Enter…"
              rows={2}
              disabled={isThinking}
              className="w-full bg-transparent resize-none px-4 pt-3 pb-1 text-sm text-slate-800 placeholder-slate-400 outline-none disabled:opacity-50"
              aria-label="Test message input"
            />
            <div className="flex items-center justify-between px-3 pb-2">
              <span className="text-xs text-slate-400">
                Enter to send · Shift+Enter for new line
              </span>
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isThinking}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                aria-label="Send message"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
